import { cn } from "../lib/utils.js";
import { FieldContext, useFieldContext } from "./field-context.js";
import { Label } from "./label.js";
import { Separator } from "./separator.js";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip.js";
import { useId, useMemo } from "react";
import { Info } from "@phosphor-icons/react";
import { jsx, jsxs } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
//#region src/components/field.tsx
const FieldSet = ({ className, ...props }) => /* @__PURE__ */ jsx("fieldset", {
	"data-slot": "field-set",
	className: cn("tw:flex tw:flex-col tw:gap-5", "tw:has-[>[data-slot=checkbox-group]]:gap-2 tw:has-[>[data-slot=radio-group]]:gap-2", className),
	...props
});
const FieldLegend = ({ className, variant = "legend", ...props }) => /* @__PURE__ */ jsx("legend", {
	"data-slot": "field-legend",
	"data-variant": variant,
	className: cn("tw:mb-2 tw:font-medium", "tw:data-[variant=legend]:text-base", "tw:data-[variant=label]:text-sm", className),
	...props
});
const FieldGroup = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	"data-slot": "field-group",
	className: cn("tw:group/field-group tw:@container/field-group", "tw:flex tw:w-full tw:flex-col", "tw:gap-6 tw:data-[slot=checkbox-group]:gap-2 tw:[&>[data-slot=field-group]]:gap-3", className),
	...props
});
const fieldVariants = cva("tw tw:group/field tw:data-[invalid=true]:text-destructive tw:flex tw:w-full tw:gap-1", {
	variants: { orientation: {
		vertical: ["tw:flex-col tw:[&>*]:w-full tw:[&>.sr-only]:w-auto"],
		horizontal: [
			"tw:flex-row tw:items-center tw:gap-2",
			"tw:[&>[data-slot=field-label]]:flex-auto",
			"tw:[&>[data-slot=field-content]]:flex-1 tw:[&>[data-slot=field-content]]:min-w-0",
			"tw:[&>[data-slot=input]]:flex-1 tw:[&>[data-slot=input]]:min-w-0",
			"tw:has-[>[data-slot=field-content]]:items-start",
			"tw:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:-mt-px"
		],
		responsive: [
			"tw:flex-col tw:[&>*]:w-full tw:[&>.sr-only]:w-auto",
			"tw:@md/field-group:flex-row tw:@md/field-group:items-center tw:@md/field-group:[&>*]:w-auto",
			"tw:@md/field-group:[&>[data-slot=field-label]]:flex-auto",
			"tw:@md/field-group:has-[>[data-slot=field-content]]:items-start",
			"tw:@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:-mt-px"
		]
	} },
	defaultVariants: { orientation: "vertical" }
});
const Field = ({ className, orientation = "vertical", ...props }) => /* @__PURE__ */ jsx("div", {
	role: "group",
	"data-slot": "field",
	"data-orientation": orientation,
	className: cn(fieldVariants({ orientation }), className),
	...props
});
const FieldContent = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	"data-slot": "field-content",
	className: cn("tw:group/field-content tw:flex tw:flex-1 tw:flex-col tw:gap-1 tw:leading-snug", className),
	...props
});
const FieldLabel = ({ className, children, htmlFor, hint, hintPlacement = "before-required", ...props }) => {
	const ctx = useFieldContext();
	return /* @__PURE__ */ jsxs(Label, {
		"data-slot": "field-label",
		id: ctx?.id ? `${ctx.id}-label` : void 0,
		htmlFor: htmlFor ?? ctx?.id,
		className: cn("tw:text-foreground", "tw:group/field-label tw:peer/field-label", "tw:block tw:w-fit", "tw:[&>[data-slot=field-label-hint]]:ml-1 tw:[&>[data-slot=field-label-hint]]:align-middle", "tw:group-data-[disabled=true]/field:opacity-50", "tw:has-[>[data-slot=field]]:w-full tw:has-[>[data-slot=field]]:flex tw:has-[>[data-slot=field]]:flex-col tw:has-[>[data-slot=field]]:gap-1", "tw:has-[>[data-slot=field]]:rounded-md tw:has-[>[data-slot=field]]:border tw:[&>[data-slot=field]]:p-4", "tw:has-data-[state=checked]:bg-primary/5 tw:has-data-[state=checked]:border-primary", "tw:dark:has-data-[state=checked]:bg-primary/10", className),
		...props,
		children: [
			children,
			hint && hintPlacement === "before-required" && /* @__PURE__ */ jsx(FieldLabelHint, { children: hint }),
			ctx?.required && /* @__PURE__ */ jsx("span", {
				className: "tw:text-destructive tw:ml-0.5",
				children: "*"
			}),
			hint && hintPlacement === "after-required" && /* @__PURE__ */ jsx(FieldLabelHint, { children: hint })
		]
	});
};
const FieldLabelHint = ({ children, ...props }) => /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, {
	asChild: true,
	children: /* @__PURE__ */ jsx("span", {
		"data-slot": "field-label-hint",
		className: "tw:inline-flex tw:items-center tw:cursor-pointer",
		children: /* @__PURE__ */ jsx(Info, { className: "tw:size-3.5 tw:text-muted-foreground" })
	})
}), /* @__PURE__ */ jsx(TooltipContent, {
	...props,
	children
})] });
const FieldTitle = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	"data-slot": "field-label",
	className: cn("tw:flex tw:w-fit tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:leading-snug tw:group-data-[disabled=true]/field:opacity-50", className),
	...props
});
const FieldDescription = ({ className, id, children, ...props }) => {
	const ctx = useFieldContext();
	const resolvedId = id ?? ctx?.descriptionId;
	return /* @__PURE__ */ jsx("p", {
		"data-slot": "field-description",
		id: resolvedId,
		className: cn("tw:text-muted-foreground tw:text-sm tw:font-normal tw:leading-snug tw:whitespace-pre-wrap", "tw:group-data-[disabled=true]/field:opacity-50", "tw:mt-0 tw:mb-0 tw:[[data-variant=legend]+&]:-mt-1.5", "tw:[&>a:hover]:text-primary tw:[&>a]:underline tw:[&>a]:underline-offset-4", className),
		...props,
		children
	});
};
const FieldSeparator = ({ children, className, ...props }) => /* @__PURE__ */ jsxs("div", {
	"data-slot": "field-separator",
	"data-content": !!children,
	className: cn("tw:relative tw:-my-2 tw:h-5 tw:text-sm tw:group-data-[variant=outline]/field-group:-mb-2", className),
	...props,
	children: [/* @__PURE__ */ jsx(Separator, { className: "tw:absolute tw:inset-0 tw:top-1/2" }), children && /* @__PURE__ */ jsx("span", {
		className: "tw:bg-background tw:text-muted-foreground tw:relative tw:mx-auto tw:block tw:w-fit tw:px-2",
		"data-slot": "field-separator-content",
		children
	})]
});
const FIELD_VALIDATION_VARIANTS = [
	"error",
	"warning",
	"success"
];
const FIELD_VALIDATION_ROLE = {
	error: "alert",
	warning: "alert",
	success: "status"
};
const FIELD_VALIDATION_CLASSES = {
	error: "tw:text-destructive",
	warning: "tw:text-amber-600 tw:dark:text-amber-400",
	success: "tw:text-secondary-moss"
};
const FieldValidation = ({ variant, children, className, id, ref, ...props }) => {
	const ctx = useFieldContext();
	const resolvedId = id ?? ctx?.validationId;
	return /* @__PURE__ */ jsx("div", {
		ref,
		role: FIELD_VALIDATION_ROLE[variant],
		"data-slot": "field-validation",
		"data-variant": variant,
		id: resolvedId,
		className: cn("tw:text-xs tw:font-normal", FIELD_VALIDATION_CLASSES[variant], "tw:[&_a]:text-current tw:[&_a]:!underline tw:[&_a]:underline-offset-4 tw:[&_a]:hover:opacity-50", className),
		...props,
		children
	});
};
const FieldError = (props) => /* @__PURE__ */ jsx(FieldValidation, {
	variant: "error",
	...props
});
const FieldRoot = ({ children, id: providedId, disabled = false, invalid = false, required = false, orientation = "vertical", className }) => {
	const generatedId = useId();
	const id = providedId ?? generatedId;
	const descriptionId = useId();
	const validationId = useId();
	const contextValue = useMemo(() => ({
		id,
		ariaDescribedBy: `${descriptionId} ${validationId}`,
		invalid,
		disabled,
		required,
		descriptionId,
		validationId
	}), [
		id,
		descriptionId,
		validationId,
		invalid,
		disabled,
		required
	]);
	return /* @__PURE__ */ jsx(FieldContext.Provider, {
		value: contextValue,
		children: /* @__PURE__ */ jsx(Field, {
			"data-disabled": disabled,
			"data-invalid": invalid,
			className,
			orientation,
			children
		})
	});
};
//#endregion
export { FIELD_VALIDATION_VARIANTS, Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldLabelHint, FieldLegend, FieldRoot, FieldSeparator, FieldSet, FieldTitle, FieldValidation };

//# sourceMappingURL=field.js.map